/* @ngInject */

export default ($q, $timeout, logger, uiService, Contact) => {
  /**
   * Delete document(s).
   *
   * @param {Object[]} _collection - Collection of documents.
   * @param {String} _collection.id - ID of document.
   * @param {Function} _persistMethod - Method to invoke in order to persist the document(s).
   * @param {Function} _modelMethod - Method to invoke in order to remove the document(s) from model.
   * @param {String} _toast - Text to show in toast.
   * @param {String} [_toastAction] - Text to show in toast as action to be performed.
   */
  function deleteDocuments(_collection, _persistMethod, _modelMethod, _toast, _toastAction) {
    var deferred = $q.defer();
    var toastAction = _toastAction || 'deleted';
    logger.log('actionService', 'deleteDocuments', 'Checking for document(s)');
    if (!_collection || !_collection.length) {
      uiService.toast({
        text: {
          p1: 'No' + _toast,
          p2: 'selected'
        },
        warn: true
      });
      logger.error('actionService', 'deleteDocuments', 'No document(s) to delete');
      deferred.reject();
    } else {
      var delayedAction = $timeout(function () {
        var queue = [];
        var len = _collection.length;
        while (len--) {
          queue.push($q.when(_persistMethod({ id: _collection[len].id })));
        }
        $q.all(queue).then(function () {
          _modelMethod(_collection);
          uiService.toast({
            text: {
              p1:
                _collection.length.formatWithoutCurrency() +
                ' selected ' +
                _toast +
                ' have been',
              p2: toastAction
            }
          });
          logger.log(
            'actionService',
            'deleteDocuments',
            'Deleted ' + _collection.length + ' document(s)'
          );
          deferred.resolve(_collection);
        });
      }, 5250);
      uiService
        .toast({
          text: {
            p1: _collection.length.formatWithoutCurrency() + ' selected ' + _toast + ' will be',
            p3: toastAction
          },
          warn: true,
          action: 'UNDO'
        })
        .then(function (_actionResult) {
          if (_actionResult) {
            $timeout.cancel(delayedAction);
            uiService.toast({
              text: {
                p1:
                  _collection.length.formatWithoutCurrency() +
                  ' selected ' +
                  _toast +
                  ' deletion',
                p3: 'undone'
              }
            });
            logger.log(
              'actionService',
              'deleteDocuments',
              'Undone deletion of ' + _collection.length + ' document(s)'
            );
            deferred.reject();
          }
        });
    }
    return deferred.promise;
  }

  /**
   * Duplicate document(s).
   *
   * @param {Object[]} _collection - Collection of documents.
   * @param {String} _collection.id - ID of document.
   * @param {Function} _readMethod - Method to invoke in order to retrieve original document(s).
   * @param {Function} _persistMethod - Method to invoke in order to persist the document(s).
   * @param {Function} _modelMethod - Method to invoke in order to append the document(s) to model.
   * @param {String} _nameProperty - Document name property.
   * @param {String} _text - Text to append to name.
   * @param {String} _toast - Text to show in toast.
   */
  function duplicateDocuments(
    _collection,
    _readMethod,
    _persistMethod,
    _modelMethod,
    _nameProperty,
    _text,
    _toast
  ) {
    var deferred = $q.defer();
    logger.log('actionService', 'duplicateDocuments', 'Checking for document(s)');
    if (!_collection || !_collection.length) {
      uiService.toast({
        text: {
          p1: 'No' + _toast,
          p2: 'selected'
        },
        warn: true
      });
      logger.error('actionService', 'duplicateDocuments', 'No document(s) to duplicate');
      deferred.reject();
    } else {
      var queue = [];
      var output = [];
      var len = _collection.length;
      uiService.toast({
        text: {
          p1: 'Duplicating',
          p2: 'Selected ' + _toast
        }
      });
      while (len--) {
        var duplicateMethod = function () {
          var innerDeferred = $q.defer();
          _readMethod({ id: _collection[len].id }).then(function (_original) {
            var _copy = angular.copy(_original);
            _copy.idClone = '';
            _copy[_nameProperty] += _text;
            if (
              _copy.docType.name === '__definition_workflow' &&
              _copy.stages &&
              _copy.stages.length
            ) {
              for (var w = 0; w < _copy.stages.length; w++) {
                _copy.stages[w].id = _.uuid();
                _copy.stages[w].idClone = '';
              }
            }
            _persistMethod(_copy).then(function (_result) {
              _copy.id = _result.id;
              output.push(_copy);
              innerDeferred.resolve();
            });
          });
          return innerDeferred.promise;
        };
        queue.push(duplicateMethod());
      }
      $q.all(queue).then(function () {
        _modelMethod(output);
        uiService.toast({
          text: {
            p1:
              _collection.length.formatWithoutCurrency() + ' selected ' + _toast + ' have been',
            p2: 'duplicated'
          }
        });
        logger.log(
          'actionService',
          'duplicateDocuments',
          'Duplicated ' + _collection.length + ' document(s)'
        );
        deferred.resolve(output);
      });
    }
    return deferred.promise;
  }

  /**
   * Toggle document(s) active state.
   *
   * @param {Object[]} _collection - Collection of documents.
   * @param {String} _collection.id - ID of document.
   * @param {Function} _persistMethod - Method to invoke in order to persist the document(s).
   * @param {Function} _modelMethod - Method to invoke in order to remove the document(s) from model.
   * @param {String} _toast - Text to show in toast.
   * @param {Boolean} _state - Set document active state to.
   */
  function toggleDocuments(_collection, _persistMethod, _modelMethod, _toast, _state) {
    var deferred = $q.defer();
    logger.log('actionService', 'toggleDocuments', 'Checking for document(s)');
    if (!_collection || !_collection.length) {
      uiService.toast({
        text: {
          p1: 'No' + _toast,
          p2: 'selected'
        },
        warn: true
      });
      logger.error('actionService', 'toggleDocuments', 'No document(s) to toggle');
      deferred.reject();
    } else {
      var queue = [];
      var len = _collection.length;
      uiService.toast({
        text: {
          p1: _state ? 'Activating' : 'Archiving',
          p2: 'selected ' + _toast
        }
      });
      while (len--) {
        if (_collection[len] instanceof Contact) {
          queue.push(
            $q.when(
              _persistMethod({
                id: _collection[len].id,
                person: {
                  information: {
                    firstName: _collection[len].person.information.firstName,
                    familyName: _collection[len].person.information.familyName
                  }
                },
                company: {
                  information: {
                    name: _collection[len].company.information.name
                  }
                },
                isCompany: _collection[len].isCompany,
                isActive: _state
              })
            )
          );
        } else {
          queue.push($q.when(_persistMethod({ id: _collection[len].id, isActive: _state })));
        }
      }
      $q.all(queue).then(function () {
        _modelMethod(_collection);
        uiService.toast({
          text: {
            p1:
              _collection.length.formatWithoutCurrency() + ' selected ' + _toast + ' have been',
            p2: _state ? 'activated' : 'archived'
          }
        });
        logger.log(
          'actionService',
          'toggleDocuments',
          'Toggled ' + _collection.length + ' document(s) state(s) to',
          _state
        );
        deferred.resolve(_collection);
      });
    }
    return deferred.promise;
  }

  return {
    deleteDocuments,
    duplicateDocuments,
    toggleDocuments
  };
};
